import { createFileRoute } from "@tanstack/react-router";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";
import { PaginasRelacionadas } from "@/components/site/PaginasRelacionadas";
import { CELL_PHONE, MAIN_PHONE, NAP, PHONES, cellWaLink, waLink } from "@/lib/whatsapp";
import { ogImageMeta } from "@/lib/og-image";
import { SocialLinks } from "@/components/site/SocialLinks";
import { PageHero } from "@/components/site/PageHero";
import { PanelSection } from "@/components/site/PanelSection";
import { PageCta } from "@/components/site/PageCta";


const TITLE = "Contacto y visitas | Residencia Oasis Cuernavaca";
const DESCRIPTION =
  "Contacte a Residencia Oasis Cuernavaca por WhatsApp o tel\u00e9fono y agende una visita guiada de lunes a domingo, 8:00 a 18:00 h. Perif\u00e9rico Sur 1, Cuernavaca.";
const PATH = "/contacto";
const MAPS =
  "https://www.google.com/maps/search/?api=1&query=Residencia+Oasis+Cuernavaca+Perif%C3%A9rico+Sur+1+Col+Emiliano+Zapata+Cuernavaca";

export const Route = createFileRoute("/contacto")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://oasiscuernavaca.com/contacto" },
      ...ogImageMeta(),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://oasiscuernavaca.com/contacto" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          webPageSchema({
            name: TITLE,
            description: DESCRIPTION,
            path: PATH,
            type: "ContactPage",
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema([{ name: "Contacto", path: PATH }])),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        breadcrumb="Contacto"
        title="Contacto y visitas a Residencia Oasis Cuernavaca"
        answer="Escríbanos por WhatsApp o llámenos: le respondemos el mismo día y agendamos la visita."
        waHref={cellWaLink}
        waLabel={`WhatsApp ${CELL_PHONE.label}`}
      />

      <PanelSection tone="soft" label="Ubicación y contacto">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <span className="panel-eyebrow">Ubicación</span>
            <h2 className="h-panel">¿Dónde estamos?</h2>
            <p className="answer-first">
              En Cuernavaca, Morelos, a unos minutos del centro y de los hospitales de la zona.
            </p>
            <address className="mt-4 not-italic">
              {NAP.name}
              <br />
              {NAP.street}
              <br />
              {NAP.locality}, {NAP.region}, C.P. {NAP.postalCode}
            </address>
            <p className="mt-4">
              <a href={MAPS} target="_blank" rel="noopener" className="link-arrow">
                Ver la ubicación en Google Maps
              </a>
            </p>
          </div>

          <div>
            <span className="panel-eyebrow">Teléfonos</span>
            <h2 className="h-panel">¿Cómo nos localiza?</h2>
            <p className="answer-first">
              Tres teléfonos y un correo. El WhatsApp es el celular {CELL_PHONE.label}.
            </p>
            <ul className="mt-4 divide-y divide-border border-t border-border">
              {PHONES.map((p) => (
                <li key={p.tel} className="py-2.5">
                  <a href={`tel:${p.tel}`} className="font-medium hover:underline">
                    {p.label}
                    <span className="ml-2 font-normal text-muted-foreground">— {p.note}</span>
                  </a>
                </li>
              ))}
              <li className="py-2.5">
                <a href={`mailto:${NAP.email}`} className="font-medium hover:underline">
                  {NAP.email}
                </a>
              </li>
            </ul>
            <SocialLinks variant="inline" className="mt-4" />
          </div>
        </div>
      </PanelSection>

      <PageCta
        waHref={waLink("contacto", "quiero agendar una visita")}
        eyebrow="La visita"
        title="¿Cómo es la visita?"
        answer="Recorre la casa, el jardín y una habitación, y platica con el equipo de cuidado. Coordinamos el horario por WhatsApp para que la visita no interrumpa comidas ni talleres. Sin compromiso ni trámites."
      />

      <PaginasRelacionadas
        items={[
          {
            to: "/precios",
            label: "Precios y costos",
            hint: "Desde $950 MXN al día según el nivel de cuidados.",
          },
          {
            to: "/preguntas-frecuentes",
            label: "Preguntas frecuentes",
            hint: "Visitas, costos, dietas y proceso de ingreso.",
          },
          {
            to: "/galeria-de-fotos",
            label: "Galería",
            hint: "Fotos reales de la residencia antes de venir.",
          },
          {
            to: "/residencia-jiutepec",
            label: "Residencia en Jiutepec",
            hint: "Registre su interés y obtenga el descuento de nueva apertura.",
          },
        ]}
      />
    </>
  );
}
